import React, { memo } from 'react';
import { AddIcon, SearchIcon, SettingsIcon } from '@chakra-ui/icons';
import { Box, HStack, Text, VStack } from '@chakra-ui/react';
import { useNavigate } from 'react-router-dom';
import { resolveCreateOrderPath } from '../../../../../routes/orderRoutes';

interface WelcomeQuickActionsProps {
  t: (key: string, options?: Record<string, unknown>) => string;
  userRole: string | null | undefined;
  /** Called right after navigating, so the panel doesn't linger over a page it no longer relates to. */
  onNavigate: () => void;
}

const ROLE_ACTIONS = [
  { key: 'find-order', icon: SearchIcon, path: '/orders/active' },
  { key: 'create-order', icon: AddIcon, path: null },
  { key: 'domain-settings', icon: SettingsIcon, path: '/settings' },
] as const;

/**
 * Static welcome-screen shortcuts to the 3 most common actions outside the
 * AI panel itself — distinct from `SuggestionCards` (the data-driven Prompt
 * Library grid used inside the "Tag a resource"/Prompt Library popup), these
 * are plain in-app navigation links, not chat prompts.
 */
export const WelcomeQuickActions: React.FC<WelcomeQuickActionsProps> = ({
  t,
  userRole,
  onNavigate,
}) => {
  const navigate = useNavigate();

  const buildPath = (action: (typeof ROLE_ACTIONS)[number]): string => {
    const role = userRole ?? 'admin';
    if (action.key === 'create-order') {
      const referrer = encodeURIComponent(`/${role}/orders/active`);
      return `/${role}${resolveCreateOrderPath(userRole)}?referrer=${referrer}`;
    }
    return `/${role}${action.path}`;
  };

  const handleSelect = (action: (typeof ROLE_ACTIONS)[number]) => {
    navigate(buildPath(action));
    onNavigate();
  };

  return (
    <VStack spacing={2} w="100%">
      {ROLE_ACTIONS.map((action) => (
        <QuickActionButton
          key={action.key}
          icon={action.icon}
          title={t(`ai.quick-actions.${action.key}.label`)}
          description={t(`ai.quick-actions.${action.key}.description`)}
          onSelect={() => handleSelect(action)}
        />
      ))}
    </VStack>
  );
};

const QuickActionButton = memo(function QuickActionButton({
  icon: Icon,
  title,
  description,
  onSelect,
}: {
  icon: React.ComponentType<{ boxSize?: number | string; color?: string }>;
  title: string;
  description: string;
  onSelect: () => void;
}) {
  return (
    <Box
      as="button"
      type="button"
      onClick={onSelect}
      w="100%"
      textAlign="start"
      px={4}
      py={3}
      borderRadius="12px"
      border="1px solid #e2e8f0"
      bg="white"
      cursor="pointer"
      transition="all 0.15s ease"
      _hover={{
        borderColor: '#93c5fd',
        bg: '#f0f7ff',
        boxShadow: '0 2px 8px rgba(59,130,246,0.10)',
      }}
    >
      <HStack spacing={3} align="start">
        <Box
          w="32px"
          h="32px"
          flexShrink={0}
          borderRadius="8px"
          bg="#e7f0ff"
          display="flex"
          alignItems="center"
          justifyContent="center"
        >
          <Icon boxSize={3.5} color="#3b82f6" />
        </Box>
        <VStack align="start" spacing={0} flex={1} minW={0}>
          <Text fontSize="13px" fontWeight="600" color="#102a43" isTruncated>
            {title}
          </Text>
          <Text fontSize="12px" color="#60758a" noOfLines={2}>
            {description}
          </Text>
        </VStack>
      </HStack>
    </Box>
  );
});
